/**
 * GeeTest v4 CAPTCHA module for Browser SDK.
 *
 * Automatically injects the GeeTest v4 script and provides
 * captcha initialization & verification helpers.
 */

// --- Types ---

/** GeeTest v4 verification result — must be sent to backend. */
export interface CaptchaResult {
  lot_number: string;
  captcha_output: string;
  pass_token: string;
  gen_time: string;
}

/** Options for initializing GeeTest captcha. */
export interface CaptchaOptions {
  /** GeeTest captchaId (site key). */
  captchaId: string;
  /** Product mode — default "bind" (popup). */
  product?: "bind" | "float" | "popup" | "custom";
  /** Language — default "zh-cn". */
  language?: string;
}

// --- Internal state ---

let captchaObj: any = null;
let initOpts: CaptchaOptions | null = null;

// Minimal window-like type for SSR safety
interface WindowLike {
  document?: Document;
  initGeetest4?: (config: any, callback: (obj: any) => void) => void;
}

declare const globalThis: WindowLike;

// --- Script injection ---

function injectScript(timeout = 10000): Promise<void> {
  if (globalThis.initGeetest4) return Promise.resolve();

  // Avoid duplicate injection
  const doc = typeof document !== "undefined" ? document : globalThis.document;
  if (!doc) return Promise.reject(new Error("GeeTest: no document available"));

  const existing = doc.querySelector('script[src*="geetest"]');
  if (existing && globalThis.initGeetest4) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const script = doc.createElement("script");
    script.src = "https://static.geetest.com/v4/gt4.js";
    script.async = true;
    const timer = setTimeout(() => reject(new Error("GeeTest SDK 加载超时")), timeout);
    script.onload = () => {
      clearTimeout(timer);
      resolve();
    };
    script.onerror = () => {
      clearTimeout(timer);
      reject(new Error("GeeTest SDK 加载失败，请检查网络"));
    };
    const target = doc.head || doc.getElementsByTagName("head")[0];
    if (target) {
      target.appendChild(script);
    } else {
      clearTimeout(timer);
      reject(new Error("GeeTest: cannot inject script — no <head> element"));
    }
  });
}

// --- Public API ---

/**
 * Initialize GeeTest captcha. Must be called before `verifyCaptcha()`.
 * Usually called automatically by `BrowserAuthClient` when `captchaId` is provided.
 */
export async function initCaptcha(opts: CaptchaOptions): Promise<void> {
  initOpts = opts;

  await injectScript();

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("GeeTest 初始化超时")), 15000);

    globalThis.initGeetest4!(
      {
        captchaId: opts.captchaId,
        product: opts.product ?? "bind",
        language: opts.language ?? "zh-cn",
      },
      (obj: any) => {
        obj.onReady(() => {
          clearTimeout(timer);
          captchaObj = obj;
          resolve();
        });
        obj.onError(() => {
          clearTimeout(timer);
          captchaObj = null;
          reject(new Error("GeeTest 初始化失败"));
        });
      },
    );
  });
}

/**
 * Show GeeTest captcha and return the verification result.
 * Throws if the user closes the captcha or an error occurs.
 */
export async function verifyCaptcha(): Promise<CaptchaResult> {
  if (!captchaObj) {
    if (!initOpts) {
      throw new Error("GeeTest 未初始化，请先调用 initCaptcha()");
    }
    await initCaptcha(initOpts);
  }

  return new Promise<CaptchaResult>((resolve, reject) => {
    captchaObj.onSuccess(() => {
      const result = captchaObj.getValidate();
      resolve({
        lot_number: result.lot_number,
        captcha_output: result.captcha_output,
        pass_token: result.pass_token,
        gen_time: result.gen_time,
      });
    });
    captchaObj.onClose(() => {
      reject(new Error("请完成图形验证"));
    });
    captchaObj.onError(() => {
      reject(new Error("图形验证出错"));
    });
    captchaObj.showCaptcha();
  });
}
